/**
 * Input normalisation for bilingual entry.
 *
 * Arabic keyboards and pasted Excel cells routinely carry Arabic-Indic digits
 * (U+0660-0669) or the Eastern/Persian variants (U+06F0-06F9), plus the Arabic
 * decimal separator U+066B and thousands separator U+066C. Xero accepts only
 * Western digits, so every numeric string is folded before it reaches a payload.
 */

const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;

/** Fold Arabic-Indic and Eastern Arabic-Indic digits to ASCII 0-9. */
export function toWesternDigits(input: string): string {
  let out = '';
  for (const ch of input) {
    const code = ch.codePointAt(0)!;
    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      out += String(code - ARABIC_INDIC_ZERO);
    } else if (code >= EXTENDED_ARABIC_INDIC_ZERO && code <= EXTENDED_ARABIC_INDIC_ZERO + 9) {
      out += String(code - EXTENDED_ARABIC_INDIC_ZERO);
    } else {
      out += ch;
    }
  }
  return out;
}

/** True when the string carries any Arabic-Indic digit. */
export function hasArabicIndicDigits(input: string): boolean {
  for (const ch of input) {
    const code = ch.codePointAt(0)!;
    if (
      (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) ||
      (code >= EXTENDED_ARABIC_INDIC_ZERO && code <= EXTENDED_ARABIC_INDIC_ZERO + 9)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Parse a user-typed amount into a number.
 *
 * Handles Arabic-Indic digits, the Arabic decimal separator (U+066B), Arabic
 * and Western thousands separators, and stray RTL/LTR marks that Windows and
 * Excel insert around numbers in mixed-direction text.
 */
export function parseAmount(input: string | number | null | undefined): number {
  if (typeof input === 'number') return Number.isFinite(input) ? input : 0;
  if (input == null) return 0;

  const cleaned = toWesternDigits(String(input))
    .replace(/[‎‏‪-‮⁦-⁩]/g, '')
    .replace(/[٬,\s]/g, '')
    .replace(/٫/g, '.');

  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : 0;
}

/**
 * Normalise a date to Xero's YYYY-MM-DD.
 * Accepts Arabic-Indic digits and the common DD/MM/YYYY entry used in Qatar.
 */
export function normaliseDate(input: string | null | undefined): string {
  if (!input) return '';
  const s = toWesternDigits(String(input).trim());

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const slash = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (slash) {
    const [, day, month, year] = slash;
    return `${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`;
  }

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return '';
}

/** Format a number the way every screen displays money: Western digits, 2dp. */
export function formatMoney(value: number): string {
  return (Math.round(value * 100) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
